import Box from '@mui/material/Box';
import { CatalogueFilters } from './CatalogueFilters';
import { CatalogueGrid } from './CatalogueGrid';
import { SearchAndSort } from './SearchAndSort';
import { useDebounce } from '../../hooks/useDebounce';
import { Item } from 'functions/src/types/firestore/Game/Collection/Item';
import { UserProfile } from 'functions/src/types/firestore/User/UserProfile';
import { useCurrency } from '../../contexts/CurrencyContext';
import { CryptoChain } from '../../util/CryptoChain';
import { Game } from 'functions/src/types/firestore/Game';
import { useRouter } from 'next/router';
import { toKebabCase } from '../../util/string';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ExtendedItemCardPriced } from './CatalogueGridItems';
import { useMobile } from 'src/hooks/useMobile';

export type Filter = {
  values: (string | number)[];
  enabled: boolean;
};

type ItemCatalogueProps = {
  parent: 'liked' | 'inventory' | 'game';
  user?: UserProfile<Date>;
  forwardedSearchQuery?: string;
  currentSortOptions?: SortOption;
} & Partial<Pick<Game, 'title' | 'id'>>;

type SortOption = {
  name: string;
  sortFunction: (
    a: ExtendedItemCardPriced,
    b: ExtendedItemCardPriced,
  ) => number;
};

function compareBigInts(
  a?: string | number | bigint | boolean | null,
  b?: string | number | bigint | boolean | null,
  order: 'asc' | 'desc' = 'asc',
) {
  const aExists = a || a === 0;
  const bExists = b || b === 0;
  if (aExists && bExists) {
    return BigInt(a) < BigInt(b) ? -1 : 1;
  }
  if (aExists && !bExists) {
    return order === 'asc' ? -1 : 1;
  }
  return order === 'asc' ? 1 : -1;
}

const SORT_OPTIONS: SortOption[] = [
  // {
  //   name: 'Date: Old to New',
  //   sortFunction: (a, b) => a..createdAt - b.createdAt,
  // },
  // {
  //   name: 'Date: New to Old',
  //   sortFunction: (a, b) => b.createdAt - a.createdAt,
  // },
  {
    name: 'Price: Low to High',
    sortFunction: (a, b) => {
      return compareBigInts(a.lowestAsk, b.lowestAsk);
    },
  },
  {
    name: 'Price: High to Low',
    sortFunction: (a, b) => {
      return compareBigInts(b.lowestAsk, a.lowestAsk, 'desc');
    },
  },
  {
    name: 'Daily Price: Low to High',
    sortFunction: (a, b) => {
      return compareBigInts(a.priceHourly, b.priceHourly);
    },
  },
  {
    name: 'Daily Price: High to Low',
    sortFunction: (a, b) => {
      return compareBigInts(b.priceHourly, a.priceHourly, 'desc');
    },
  },
  {
    name: 'Earnings Royalty: Low to High',
    sortFunction: (a, b) => {
      return compareBigInts(a.royaltyRental, b.royaltyRental);
    },
  },
  {
    name: 'Earnings Royalty: High to Low',
    sortFunction: (a, b) => {
      return compareBigInts(b.royaltyRental, a.royaltyRental, 'desc');
    },
  },
  {
    name: 'Likes: Most to Least',
    sortFunction: (a, b) => {
      return b.likes - a.likes;
    },
  },
  {
    name: 'Likes: Least to Most',
    sortFunction: (a, b) => {
      return a.likes - b.likes;
    },
  },
];

export function ItemCatalogue({
  parent,
  user,
  forwardedSearchQuery,
  currentSortOptions,
  title,
  id,
}: ItemCatalogueProps) {
  const isMobile = useMobile();
  const currency = useCurrency();
  const router = useRouter();
  const { filters } = router.query;
  const urlSearchQuery = router.query.searchQuery;
  const urlSortOption = router.query.sort;
  const [sortSetFromQuery, setSortSetFromQuery] = useState(false);
  const [searchSetFromQuery, setSearchSetFromQuery] = useState(false);
  const [filterStateFromQuery, setFilterStateFromQuery] = useState({});
  const [filtersSetFromQuery, setFiltersSetFromQuery] = useState(false);
  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    if (filters && ref?.current && !filtersSetFromQuery) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filters, ref, filtersSetFromQuery]);

  useEffect(() => {
    if (!filters) {
      setFiltersSetFromQuery(true);
      return;
    }
    // if there is only one filter, we need to cast it to an array
    const correctedFilters: string[] = [];
    if (typeof filters === 'string') {
      correctedFilters.push(filters);
    } else {
      correctedFilters.push(...filters);
    }
    const toNumber = (s: string) => {
      const n = Number(s);
      if (Number.isNaN(n)) {
        return s;
      }
      return n;
    };
    if (!filtersSetFromQuery && filters && filters.length > 0) {
      const parsedState = {};
      correctedFilters.forEach((filter) => {
        const [name, valueString] = filter.split(':');
        const values = valueString.split(',');
        if (values.length > 1) {
          parsedState[`${name}`] = {
            values: values.map(toNumber),
            enabled: true,
          };
        } else {
          parsedState[`${name}`] = {
            values: values[0] === 'true' ? [] : values,
            enabled: true,
          };
        }
      });
      setFilterStateFromQuery(parsedState);
      setFilterState(parsedState);
      setFiltersSetFromQuery(true);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const [filterState, setFilterState] = useState<{
    [name: string]: Filter;
  }>({});
  useEffect(() => {
    if (!urlSearchQuery || searchSetFromQuery) {
      return;
    }
    setSearchQuery(urlSearchQuery as string);
    setSearchSetFromQuery(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchQuery]);
  useEffect(() => {
    if (!urlSortOption || sortSetFromQuery) {
      return;
    }
    const found = SORT_OPTIONS.find((option) => {
      return option.name === urlSortOption;
    });
    setCurrentSortOptions(found as SortOption);
    setSortSetFromQuery(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSortOption]);
  useEffect(() => {
    if (router && filtersSetFromQuery) {
      const filterStrings: string[] = [];
      for (const [key, value] of Object.entries(filterState)) {
        if (value.enabled) {
          filterStrings.push(
            `${key}:${
              value.values.length > 0 ? value.values.join(',') : 'true'
            }`,
          );
        }
      }
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            pid:
              parent === 'game' && title
                ? toKebabCase(title)
                : router.query.pid,
            filters: filterStrings,
          },
        },
        undefined,
        { shallow: true },
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState, filtersSetFromQuery, title]);
  const [currentSortOption, setCurrentSortOptions] = useState<SortOption>(
    SORT_OPTIONS[0],
  );
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  useEffect(() => {
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, searchQuery: debouncedSearchQuery },
      },
      undefined,
      { shallow: true },
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  const debouncedForwardedSearchQuery = useDebounce(forwardedSearchQuery, 500);

  const handleFilterChange = (name, value) => {
    setFilterState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };
  const handleSortChange = (e) => {
    const newSortOptions = SORT_OPTIONS.find((option) => {
      return option.name === e.target.value;
    }) as SortOption;
    setCurrentSortOptions(newSortOptions);
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, sort: newSortOptions.name },
      },
      undefined,
      { shallow: true },
    );
  };
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  const searchFunction = useMemo(() => {
    return (input: Item) => {
      const query = debouncedForwardedSearchQuery || debouncedSearchQuery;
      if (!query) {
        return true;
      }
      return (
        `${input.title} #${input.id}`.toLowerCase().includes(query) ||
        input.description?.toLowerCase().includes(query)
      );
    };
  }, [debouncedForwardedSearchQuery, debouncedSearchQuery]);
  const supportedChains = [CryptoChain.from(43113)];
  const [selectedSaleType, setSelectedSaleType] = useState<
    'buy' | 'rent' | null
  >(null);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: parent === 'game' ? 0 : 8,
      }}
    >
      {parent === 'game' && (
        <Box ref={ref} sx={{ px: 6, mb: 10, mt: -5 }}>
          <SearchAndSort
            sortOptions={SORT_OPTIONS}
            currentSortOption={currentSortOptions || currentSortOption}
            searchQuery={forwardedSearchQuery || searchQuery}
            handleSearchChange={handleSearchChange}
            handleSortChange={handleSortChange}
            selectedSaleType={selectedSaleType}
            select={(saleType) => {
              return setSelectedSaleType(
                saleType === selectedSaleType ? null : saleType,
              );
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: parent === 'liked' ? (isMobile ? 0 : 10) : 4,
        }}
      >
        <CatalogueFilters
          filtersFromQuery={filterStateFromQuery}
          title={title}
          currency={currency}
          parent={parent}
          handleFilterChange={handleFilterChange}
        />
        <CatalogueGrid
          isDragEnabled={false}
          likedPaths={user?.likedPaths}
          id={id}
          title={title}
          parent={parent}
          currency={currency}
          filters={filterState}
          supportedChains={supportedChains}
          sortFunction={
            currentSortOptions
              ? currentSortOptions.sortFunction
              : currentSortOption.sortFunction
          }
          searchFunction={searchFunction}
          selectedSaleType={selectedSaleType}
        />
      </Box>
    </Box>
  );
}
